// src/context/useDropdownContext.ts
import { provide, inject, Ref, ref, reactive } from 'vue'

export type DropdownContextProps = {
  initial?: string
}

export type DropdownContextType = {
  // existing API
  current?: Ref<string>
  toggle: (index: string) => void
  set: (index: string) => void

  // NEW: multi-open support for top-level (depth 0)
  openTopLevel: Set<number>
  openTopLevelOnly: (indices: number[]) => void
  openTopLevelAlso: (indices: number[]) => void
  closeTopLevel: (indices: number[]) => void
  clearTopLevel: () => void
  isTopLevelOpen: (index: number) => boolean
}

export const DROPDOWN_INJECTION_KEY = Symbol('DROPDOWN')

export const useDropdownContext = (options?: DropdownContextProps) => {
  // single-select (kept for inner levels)
  const current = ref(options?.initial || '')

  const set = (index: string) => {
    current.value = index
  }

  const toggle = (index: string) => {
    current.value = index === current.value ? '' : index
  }

  // NEW: multi-open set for top-level dropdowns
  const openTopLevel = reactive(new Set<number>())

  const openTopLevelOnly = (indices: number[]) => {
    openTopLevel.clear()
    indices.forEach((i) => openTopLevel.add(i))
  }

  const openTopLevelAlso = (indices: number[]) => {
    indices.forEach((i) => openTopLevel.add(i))
  }

  const closeTopLevel = (indices: number[]) => {
    indices.forEach((i) => openTopLevel.delete(i))
  }

  const clearTopLevel = () => openTopLevel.clear()

  const isTopLevelOpen = (index: number) => openTopLevel.has(index)

  const values: DropdownContextType = {
    set,
    toggle,
    current,

    openTopLevel,
    openTopLevelOnly,
    openTopLevelAlso,
    closeTopLevel,
    clearTopLevel,
    isTopLevelOpen,
  }

  provide<DropdownContextType>(DROPDOWN_INJECTION_KEY, values)
  return values
}

export const useDropdownInject = () => {
  const context = inject<DropdownContextType>(DROPDOWN_INJECTION_KEY)
  if (!context) throw new Error('Dropdown context not provided')
  return context
}
